import React, { useEffect } from 'react';
import { Layout } from '@/components/Common';
import CustomHead from '@/components/Common/Layout/Head';
import { NextPageWithLayout } from '@/pages/_app';
import type { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import Script from 'next/script';
import { WEBSITE_URL } from '@/shared/constants';
import {
  getCatalogCompanyReviews,
  getCatalogCompanyReviewSummary,
  getCatalogItemByHandle,
  getCatalogItems,
} from '@/lib/service/catalog';
import { CatalogItem, SupplierReview } from '@/shared/types/catalog';
import { businessSettingsDisplay, logPageView } from '@/lib/service';
import ProductDetailsPage from '@/components/SourcingMarketplace/ProductDetail/ProductDetailsPage';
import { BusinessSettingConfigKey } from '@/shared/types/business-setting';
import { CategoryConfig } from '@/shared/types/category';
import { getSupplierName, isMatchingCategory } from '@/shared/utils/sourcing-marketplace-util';
import { getBreadcrumbListJsonLd, getProductJsonLd } from '@/shared/seo/jsonld';
import type { SupplierReviewSummaryResponse } from '@/shared/types/api/catalog';
import { first, trim, truncate } from 'lodash-es';
import { SOURCING_PRODUCT_NAME_FOR_ISR } from '@/shared/constants/sourcing-marketplace';
import { isStaging } from '@/shared/utils/utils';
import cloudfrontLoader from '@/cloudfront-loader';
import { useHistoryList } from '@/lib/context/HistoryListProvider';

export const getStaticPaths = async () => {
  let paths = [];

  if (isStaging) {
    paths.push({ params: { handle: 'see-through-panel-shirt-and-shorts', device: ['m'] } });
    paths.push({ params: { handle: 'see-through-panel-shirt-and-shorts', device: [] } });
  } else {
    SOURCING_PRODUCT_NAME_FOR_ISR.forEach((handle) => {
      paths.push({ params: { handle, device: ['m'] } });
      paths.push({ params: { handle, device: [] } });
    });
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps = async (context: GetStaticPropsContext) => {
  try {
    const { handle, device } = context.params;
    const product: CatalogItem = await getCatalogItemByHandle(handle as string);

    if (!product || !product.enabledAt) {
      return { notFound: true };
    }

    const [{ list }, configResponse, reviewSummary, reviewsResponse] = await Promise.all([
      getCatalogItems({
        size: 25,
        page: 0,
        catalogCompanyId: [product.catalogCompanyId],
      }),
      businessSettingsDisplay(BusinessSettingConfigKey.CREATOR_HUB_HOMEPAGE_CATEGORIES_V2),
      getCatalogCompanyReviewSummary(product.catalogCompanyId),
      getCatalogCompanyReviews({
        page: 0,
        size: 25,
        companyId: product.catalogCompanyId,
        sorts: '-createdAt',
      }),
    ]);

    const factorySpecialties = Array.from(new Set(list?.map((x) => x.catalogCategory.subgroup)));
    const config: { [key: string]: CategoryConfig[] } = configResponse?.value;
    const allConfigs = Object.values(config).flat();
    const matchingCategory = allConfigs.find((c: CategoryConfig) => isMatchingCategory(c, product.catalogCategory));
    return {
      props: {
        product,
        factorySpecialties,
        categoryConfig: matchingCategory || null,
        reviewSummary,
        reviews: reviewsResponse?.list,
        isMobileURL: !!device?.includes('m'),
      },
      revalidate: 3600,
    };
  } catch (e) {
    console.error('Error fetching product', e);
    return { notFound: true };
  }
};

const getPageTitle = (product: CatalogItem, categoryConfig: CategoryConfig) => {
  return `${product?.name} - ${categoryConfig?.title} - by ${getSupplierName(product?.catalogCompany)} | Pietra`;
};

const Page: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = ({
  product,
  factorySpecialties,
  categoryConfig,
  reviewSummary,
  reviews,
  isMobileURL,
}: {
  product: CatalogItem;
  factorySpecialties: string[];
  categoryConfig: CategoryConfig | null;
  reviewSummary: SupplierReviewSummaryResponse;
  reviews: SupplierReview[];
  isMobileURL: boolean;
}) => {
  const { sourcingBreadcrumbs, setSourcingBreadcrumbs } = useHistoryList();

  useEffect(() => {
    if (product?.name) {
      logPageView({
        pageUrl: window.location.href || '',
        routeName: window.location.pathname,
        pageTitle: getPageTitle(product, categoryConfig),
      });
    }
  }, [categoryConfig, product, product?.name]);

  useEffect(() => {
    if (product) {
      const breadcrumbs = sourcingBreadcrumbs.filter(
        (breadcrumb) => breadcrumb?.type !== 'productName' && breadcrumb?.type !== 'supplierName',
      );
      const company = product?.catalogCompany;
      setSourcingBreadcrumbs([
        ...breadcrumbs,
        {
          type: 'supplierName',
          label: company?.name,
          supplierId: company?.handle,
        },
        {
          type: 'productName',
          label: product?.name,
        },
      ]);
    }
  }, [product]);

  return (
    <ProductDetailsPage
      factorySpecialties={factorySpecialties}
      product={product}
      reviews={reviews}
      reviewSummary={reviewSummary}
      categoryConfig={categoryConfig}
      isMobileURL={isMobileURL}
    />
  );
};

Page.getLayout = (page: React.ReactElement) => {
  const { product, reviewSummary, reviews, categoryConfig } = page?.props || Object.create(null);

  const url = `${WEBSITE_URL}/sourcing/products/${product.handle}`;
  const description = `You will receive: ${
    product?.meta?.sampleOrder || 'The product you have just designed with your customizations.'
  } ${trim(product?.meta?.description, '\n')}`;

  const firstImage = product?.images ? cloudfrontLoader({ src: first(product.images), quality: 75, width: 382 }) : null;

  return (
    <>
      <CustomHead
        title={getPageTitle(product, categoryConfig)}
        description={truncate(description, {
          length: 160,
          omission: '...',
        })}
        url={url}
        image={firstImage}
      />
      {firstImage && <link rel="preload" fetchPriority="high" as="image" href={firstImage} />}
      <Layout hideFooter>{page}</Layout>
      <Script
        id="product-jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            getProductJsonLd(product, reviewSummary?.score, reviewSummary?.count, reviews),
            getBreadcrumbListJsonLd([
              { id: `${WEBSITE_URL}/sourcing`, name: 'All categories' },
              { id: `/sourcing/browse/${categoryConfig?.key}/products`, name: categoryConfig?.title },
              {
                id: `/sourcing/suppliers/${product.catalogCompany.handle}`,
                name: getSupplierName(product.catalogCompany),
              },
              { id: url, name: product.name },
            ]),
          ]),
        }}
      />
    </>
  );
};

export default Page;
